import React from "react";
import {NavLink} from "react-router-dom";
import {AnimatePresence, motion} from "framer-motion";
import {twMerge} from "tailwind-merge";
import {FaCommentDots, FaMicrophone, FaProjectDiagram, FaYoutube} from "react-icons/fa";
import {Tooltip} from '@heroui/react';
import {useRecoilValue} from "recoil";
import {sidebarExpandedState} from "../store.jsx";

const navItems = [
	{
		path: "/app",
		label: "Create Dub",
		icon: <FaProjectDiagram/>,
		gradient: "from-violet-500 to-indigo-600",
		glow: "rgba(124,58,237,0.55)"
	},
	{
		path: "/voices",
		label: "Voices",
		icon: <FaMicrophone/>,
		gradient: "from-emerald-500 to-teal-600",
		glow: "rgba(16,185,129,0.5)"
	},
	{
		path: "/text-to-speech",
		label: "AI Speech",
		icon: <FaCommentDots/>,
		gradient: "from-fuchsia-500 to-pink-600",
		glow: "rgba(217,70,239,0.55)"
	},
	{
		path: "/auto-dub",
		label: "Auto-Dub",
		icon: <FaYoutube/>,
		gradient: "from-red-500 to-rose-600",
		glow: "rgba(244,63,94,0.55)"
	}
];

const EASE = [0.4, 0, 0.2, 1];
const sidebarVariants = {
	expanded: {width: 280, transition: {duration: 0.26, ease: EASE}},
	collapsed: {width: 80, transition: {duration: 0.26, ease: EASE}}
};

export default function SidebarNavigation() {
	const isExpanded = useRecoilValue(sidebarExpandedState);

	return (
		<motion.nav
			initial="collapsed"
			animate={isExpanded ? "expanded" : "collapsed"}
			variants={sidebarVariants}
			className={twMerge(`
				h-screen sticky top-0
				bg-white/55 backdrop-blur-2xl
				border-r border-white/40
				shadow-[8px_0_40px_-12px_rgba(90,10,224,0.18)]
				relative z-40
				flex flex-col
			`)}
		>
			{/* Aurora backdrop */}
			<div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
				<motion.div
					className="absolute -top-24 -left-16 h-72 w-72 rounded-full bg-primary/30 blur-3xl"
					animate={{y: [0, 30, 0], x: [0, 14, 0]}}
					transition={{duration: 12, repeat: Infinity, ease: "easeInOut"}}
				/>
				<motion.div
					className="absolute top-1/3 -right-20 h-80 w-80 rounded-full bg-fuchsia-400/25 blur-3xl"
					animate={{y: [0, -36, 0], x: [0, -10, 0]}}
					transition={{duration: 14, repeat: Infinity, ease: "easeInOut"}}
				/>
				<motion.div
					className="absolute -bottom-24 left-0 h-72 w-72 rounded-full bg-indigo-400/20 blur-3xl"
					animate={{y: [0, -24, 0]}}
					transition={{duration: 16, repeat: Infinity, ease: "easeInOut"}}
				/>
			</div>

			{/* Header / brand — FIXED height so the nav below never shifts when toggling.
			    Logos are absolutely positioned and crossfade in place (no "wait"),
			    so the wordmark appears immediately on expand instead of lagging. */}
			<div className="relative h-[84px] shrink-0">
				<AnimatePresence initial={false}>
					{isExpanded ? (
						<motion.img
							key="full"
							src="/images/logo-full.png"
							alt="AudioDub.ai"
							initial={{opacity: 0}}
							animate={{opacity: 1}}
							exit={{opacity: 0}}
							transition={{duration: 0.18}}
							className="absolute left-4 top-1/2 -mt-4 h-8 w-auto object-contain"
						/>
					) : (
						<motion.div
							key="icon"
							initial={{opacity: 0, scale: 0.6}}
							animate={{opacity: 1, scale: 1}}
							exit={{opacity: 0, scale: 0.6}}
							transition={{type: "spring", stiffness: 360, damping: 22}}
							whileHover={{scale: 1.08, rotate: -3}}
							className="absolute left-1/2 top-1/2 -ml-[22px] -mt-[22px] grid h-11 w-11 place-items-center rounded-2xl bg-white shadow-md shadow-primary/15 ring-1 ring-black/5"
						>
							<motion.img
								src="/images/logo-icon.png"
								alt="AudioDub.ai"
								animate={{scale: [1, 1.07, 1]}}
								transition={{duration: 3.2, repeat: Infinity, ease: "easeInOut"}}
								className="h-7 w-7 object-contain"
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			{/* Navigation */}
			<div className="flex-1 px-3 py-2 overflow-y-auto overflow-x-hidden no-scrollbar">
				<ul className="space-y-1.5">
					{navItems.map((item, idx) => (
						<motion.li
							key={item.path}
							initial={{opacity: 0, x: -16}}
							animate={{opacity: 1, x: 0}}
							transition={{delay: idx * 0.05, type: "spring", stiffness: 320, damping: 26}}
						>
							<Tooltip
								content={item.label} showArrow
								placement="right" color="foreground"
								offset={14} delay={300} isDisabled={isExpanded}
								classNames={{content: 'py-2 px-3 font-medium'}}
							>
								<NavLink to={item.path}>
									{({isActive}) => (
										<div
											className={twMerge(
												// pl-4 centers the 24px icon in the 80px collapsed rail (12px container + 16px = center 40);
												// constant in both states → icon stays put, no horizontal jump on resize
												"group relative flex items-center rounded-2xl py-3 pl-4 pr-3 transition-colors duration-200",
												isActive ? 'text-white' : 'text-slate-600 hover:text-slate-900'
											)}
										>
											{/* Sliding active background with sheen */}
											{isActive && (
												<motion.div
													layoutId="sidebar-active-pill"
													className={twMerge(`absolute inset-0 overflow-hidden rounded-2xl bg-gradient-to-r ${item.gradient}`)}
													style={{boxShadow: `0 10px 30px -8px ${item.glow}`}}
													transition={{type: "spring", stiffness: 380, damping: 32}}
												>
													<motion.span
														aria-hidden
														className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/35 to-transparent"
														initial={{x: "-120%"}}
														animate={{x: "120%"}}
														transition={{duration: 1.6, repeat: Infinity, repeatDelay: 1.4, ease: "easeInOut"}}
													/>
												</motion.div>
											)}

											{/* Idle hover wash */}
											{!isActive && (
												<span className="absolute inset-0 rounded-2xl bg-slate-900/0 group-hover:bg-slate-900/[0.05] transition-colors"/>
											)}

											<motion.span
												className="relative z-10 grid h-6 w-6 place-items-center text-[19px]"
												whileHover={{scale: 1.15, rotate: -4}}
												transition={{type: "spring", stiffness: 400, damping: 12}}
											>
												{item.icon}
											</motion.span>

											<motion.span
												animate={{opacity: isExpanded ? 1 : 0}}
												transition={{duration: isExpanded ? 0.2 : 0.1, delay: isExpanded ? 0.08 : 0}}
												className="relative z-10 ml-3 text-sm font-semibold whitespace-nowrap pointer-events-none"
											>
												{item.label}
											</motion.span>
										</div>
									)}
								</NavLink>
							</Tooltip>
						</motion.li>
					))}
				</ul>
			</div>

			{/* Footer */}
			<div className="px-3.5 pb-5 pt-3">
				<div className="relative overflow-hidden rounded-2xl border border-white/50 bg-white/40 px-3 py-3 backdrop-blur">
					<AnimatePresence mode="wait">
						{isExpanded ? (
							<motion.div
								key="full"
								initial={{opacity: 0}}
								animate={{opacity: 1}}
								exit={{opacity: 0}}
								className="flex items-center gap-2.5"
							>
								<span className="relative flex h-2.5 w-2.5">
									<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"/>
									<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"/>
								</span>
								<div className="leading-tight">
									<p className="text-[11px] font-semibold text-slate-700">All systems live</p>
									<p className="text-[10px] text-slate-400">© {new Date().getFullYear()} AudioDub.ai</p>
								</div>
							</motion.div>
						) : (
							<motion.div key="dot" initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="flex justify-center">
								<span className="relative flex h-2.5 w-2.5">
									<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"/>
									<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"/>
								</span>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</div>
		</motion.nav>
	);
}
